/**
 * Briefing (the prototype's Briefing dialog, web.md §4): tab "This view" lists the current results with
 * the written assessment when one exists, else the source preview — it never generates anything; tab
 * "Saved digests" lists the digests the user's assistant saved over MCP, newest first, each readable in
 * place. Digest Markdown is rendered as text blocks (headings, lists, paragraphs), never as HTML.
 */

import { useQueries } from '@tanstack/react-query';
import { Fragment, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DigestId, ListQuery, MessageId } from '@digester/core';
import { DialogFrame, type DialogProps } from '../app/dialogs.js';
import { messagePath, type ReaderScope } from '../app/reader-route.js';
import { useEngine, useEngineQuery } from '../engine-client/EngineProvider.js';
import { qk } from '../engine-client/keys.js';
import { Icon } from '../icons/Icon.js';
import { useLookups, useMessageList, useCorrectionsList } from './data.js';
import { eventTime } from './format.js';

export interface BriefingDialogProps {
  query: Omit<ListQuery, 'cursor'>;
  scope: ReaderScope;
  title: string;
  timeZone?: string;
}

/** How many messages of the view the briefing shows. */
const BRIEF_LIMIT = 40;

type Tab = 'view' | 'digests';

export function BriefingDialog({ close, dialogId, query, scope, title, timeZone }: DialogProps & BriefingDialogProps) {
  const [tab, setTab] = useState<Tab>('view');
  return (
    <DialogFrame title="Briefing" className="brief-dialog" onClose={close} dialogId={dialogId}>
      <div className="brief-tabs" role="tablist" aria-label="Briefing">
        <button type="button" role="tab" id="brief-tab-view" aria-selected={tab === 'view'} aria-controls="brief-panel" onClick={() => setTab('view')}>
          This view
        </button>
        <button type="button" role="tab" id="brief-tab-digests" aria-selected={tab === 'digests'} aria-controls="brief-panel" onClick={() => setTab('digests')}>
          Saved digests
        </button>
      </div>
      <div id="brief-panel" role="tabpanel" aria-labelledby={tab === 'view' ? 'brief-tab-view' : 'brief-tab-digests'}>
        {tab === 'view' ? <ThisView query={query} scope={scope} title={title} close={close} /> : <SavedDigests timeZone={timeZone} />}
      </div>
    </DialogFrame>
  );
}

function ThisView({ query, scope, title, close }: { query: Omit<ListQuery, 'cursor'>; scope: ReaderScope; title: string; close(): void }) {
  const engine = useEngine();
  const navigate = useNavigate();
  const lookups = useLookups();
  const isCorrections = scope.kind === 'corrections';
  const messages = useMessageList(query, !isCorrections);
  // The Corrections scope is not a message query: brief its open corrections' messages instead.
  const corrections = useCorrectionsList(isCorrections, lookups.filters, '');
  const list = isCorrections ? corrections : messages;
  const rows = list.rows.slice(0, BRIEF_LIMIT);
  const details = useQueries({ queries: rows.map((r) => ({ queryKey: qk.message(r.id), queryFn: () => engine.messages.get(r.id) })) });
  const open = (id: MessageId): void => {
    close();
    navigate(messagePath(scope, id));
  };
  if (list.loading) return null;
  if (!rows.length) return <p className="subtle">No messages in this view.</p>;
  return (
    <>
      <p className="brief-scope subtle">
        {title} · {list.total > rows.length ? `first ${rows.length} of ${list.total}` : `${rows.length} ${rows.length === 1 ? 'message' : 'messages'}`}
      </p>
      <div className="brief-list">
        {rows.map((m, i) => {
          const assessment = details[i]?.data?.assessment ?? null;
          return (
            <article key={m.id}>
              <button type="button" onClick={() => open(m.id)}>
                {m.title ?? m.excerpt}
              </button>
              <p>{assessment ? assessment.body : m.excerpt}</p>
              <small>
                {lookups.source(m.sourceId)?.name ?? ''} · {assessment ? 'Assistant assessment' : 'Source preview'}
              </small>
            </article>
          );
        })}
      </div>
    </>
  );
}

function SavedDigests({ timeZone }: { timeZone?: string }) {
  const page = useEngineQuery(qk.digests({ limit: 50 }), (e) => e.digests.list(50));
  const [openId, setOpenId] = useState<DigestId | null>(null);
  if (page.isLoading) return null;
  const items = page.data?.items ?? [];
  if (!items.length)
    return (
      <p className="subtle">
        No saved digests yet. Your assistant saves them over MCP (for example the morning digest); they appear here.
      </p>
    );
  return (
    <div className="brief-list digest-list">
      {items.map((d) => (
        <article key={d.id}>
          <button type="button" aria-expanded={openId === d.id} onClick={() => setOpenId((id) => (id === d.id ? null : d.id))}>
            {d.title}
          </button>
          <small>
            {eventTime(d.createdAt, timeZone)} · {d.messageIds.length} {d.messageIds.length === 1 ? 'message' : 'messages'}
          </small>
          {openId === d.id ? <DigestBody id={d.id} /> : null}
        </article>
      ))}
    </div>
  );
}

function DigestBody({ id }: { id: DigestId }) {
  const digest = useEngineQuery([...qk.digests(), 'one', id], (e) => e.digests.get(id));
  if (digest.isLoading) return null;
  if (!digest.data) return <p className="subtle">{digest.error?.message ?? 'This digest is no longer available.'}</p>;
  return <div className="digest-body">{markdownBlocks(digest.data.bodyMd)}</div>;
}

/** A small Markdown subset as React text: headings, bullet / numbered lists, quotes, paragraphs. */
export function markdownBlocks(md: string): ReactNode {
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = (): void => {
    if (para.length) blocks.push(<p key={blocks.length}>{inline(para.join(' '))}</p>);
    para = [];
  };
  const flushList = (): void => {
    if (!list) return;
    const items = list.items.map((t, i) => <li key={i}>{inline(t)}</li>);
    blocks.push(list.ordered ? <ol key={blocks.length}>{items}</ol> : <ul key={blocks.length}>{items}</ul>);
    list = null;
  };
  for (const raw of md.split(/\r?\n/)) {
    const line = raw.trim();
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const bullet = /^[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line);
    if (!line) {
      flushPara();
      flushList();
    } else if (heading) {
      flushPara();
      flushList();
      const level = Math.min(4, (heading[1]?.length ?? 1) + 1);
      const text = inline(heading[2] ?? '');
      blocks.push(level === 2 ? <h2 key={blocks.length}>{text}</h2> : level === 3 ? <h3 key={blocks.length}>{text}</h3> : <h4 key={blocks.length}>{text}</h4>);
    } else if (bullet || numbered) {
      flushPara();
      const ordered = !bullet;
      if (list && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push((bullet?.[1] ?? numbered?.[1] ?? '').trim());
    } else if (line.startsWith('>')) {
      flushPara();
      flushList();
      blocks.push(<blockquote key={blocks.length}>{inline(line.replace(/^>\s?/, ''))}</blockquote>);
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return blocks;
}

/** Links `[text](https://…)` become safe links; **bold** stays bold; everything else is text. */
function inline(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1] !== undefined && match[2] !== undefined)
      parts.push(
        <a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer nofollow">
          {match[1]}
          <Icon name="external-link" />
        </a>,
      );
    else parts.push(<strong key={match.index}>{match[3]}</strong>);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) => <Fragment key={i}>{p}</Fragment>);
}
